"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Trade = void 0;
const currency_1 = require("@zenlink-interface/currency");
const math_1 = require("@zenlink-interface/math");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const MultiRoute_1 = require("../MultiRoute");
const StablePool_1 = require("../StablePool");
const BaseTrade_1 = require("./BaseTrade");
const computePriceImpact_1 = require("./computePriceImpact");
const convertStableSwapOrPairToPool_1 = require("./convertStableSwapOrPairToPool");
const sortedInsert_1 = require("./sortedInsert");
const tradeComparator_1 = require("./tradeComparator");
const TradeVersion_1 = require("./TradeVersion");
class Trade {
    constructor(chainId, route, amount) {
        this.version = TradeVersion_1.TradeVersion.LEGACY;
        const amounts = new Array(route.tokenPath.length);
        (0, tiny_invariant_1.default)(amount.currency.wrapped.equals(route.input), 'INPUT');
        amounts[0] = amount.wrapped;
        for (let i = 0; i < route.tokenPath.length - 1; i++) {
            const currentPath = route.routePath[i];
            let outputAmount;
            if (currentPath.stable) {
                outputAmount = (0, StablePool_1.getStableSwapOutputAmount)(currentPath, amounts[i]);
            }
            else {
                (0, tiny_invariant_1.default)(typeof currentPath.pair !== 'undefined', 'PAIR');
                [outputAmount] = currentPath.pair.getOutputAmount(amounts[i]);
            }
            amounts[i + 1] = outputAmount;
        }
        this.chainId = chainId;
        this.route = route;
        this.inputAmount = amount;
        this.outputAmount = currency_1.WNATIVE[this.chainId].equals(amounts[amounts.length - 1].currency)
            ? currency_1.Amount.fromRawAmount(currency_1.Native.onChain(this.chainId), amounts[amounts.length - 1].quotient)
            : amounts[amounts.length - 1];
        this.executionPrice = new currency_1.Price(this.inputAmount.currency, this.outputAmount.currency, this.inputAmount.quotient, this.outputAmount.quotient);
        this.priceImpact = (0, computePriceImpact_1.computePriceImpact)(route.midPrice, this.inputAmount.wrapped, this.outputAmount.wrapped);
    }
    get descriptions() {
        return this.route.routePath.map(({ input, output, stable, pool, pair }) => ({
            input,
            output,
            fee: stable ? 0.05 : 0.3,
            poolAddress: stable ? pool?.liquidityToken.address : pair?.liquidityToken.address,
            poolType: stable ? BaseTrade_1.PoolType.Stable : BaseTrade_1.PoolType.Standard,
            absolutePortion: 1,
        }));
    }
    minimumAmountOut(slippageTolerance) {
        (0, tiny_invariant_1.default)(!slippageTolerance.lessThan(math_1.ZERO), 'SLIPPAGE_TOLERANCE');
        const slippageAdjustedAmountOut = new math_1.Fraction(math_1.ONE)
            .add(slippageTolerance)
            .invert()
            .multiply(this.outputAmount.quotient).quotient;
        return currency_1.Amount.fromRawAmount(this.outputAmount.currency.wrapped, slippageAdjustedAmountOut);
    }
    static bestTradeExactIn(chainId, pairs, stableSwaps, currencyAmountIn, currencyOut, { maxHops = 3, maxNumResults = 3 } = {}, currentPaths = [], originalAmountIn = currencyAmountIn, bestTrades = []) {
        (0, tiny_invariant_1.default)(pairs.length > 0 || stableSwaps.length > 0, 'PAIRS_OR_STABLESWAPS');
        (0, tiny_invariant_1.default)(maxHops > 0, 'MAX_HOPS');
        (0, tiny_invariant_1.default)(originalAmountIn === currencyAmountIn || currentPaths.length > 0, 'INVALID_RECURSION');
        const amountIn = currencyAmountIn.wrapped;
        const tokenOut = currencyOut.wrapped;
        if (stableSwaps.length) {
            pairs = (0, convertStableSwapOrPairToPool_1.convertStableSwapOrPairToPool)(pairs, stableSwaps);
            stableSwaps = [];
        }
        for (let i = 0; i < pairs.length; i++) {
            let amountOut;
            const pair = pairs[i];
            if (!pair.token0.equals(amountIn.currency) && !pair.token1.equals(amountIn.currency))
                continue;
            if (pair.reserve0.equalTo(math_1.ZERO) || pair.reserve1.equalTo(math_1.ZERO))
                continue;
            try {
                [amountOut] = pair.getOutputAmount(amountIn);
            }
            catch (error) {
                if (error.isInsufficientInputAmountError || error.isCalculationError)
                    continue;
                throw error;
            }
            if (amountOut.currency.equals(tokenOut)) {
                (0, sortedInsert_1.sortedInsert)(bestTrades, new Trade(chainId, new MultiRoute_1.MultiRoute(chainId, [...currentPaths, pair.pathOf(amountIn.currency)], originalAmountIn.wrapped, currencyOut.wrapped), originalAmountIn), maxNumResults, tradeComparator_1.tradeComparator);
            }
            else if (maxHops > 1 && pairs.length > 1) {
                const pairsExcludingThisPair = pairs.slice(0, i).concat(pairs.slice(i + 1, pairs.length));
                Trade.bestTradeExactIn(chainId, pairsExcludingThisPair, stableSwaps, amountOut, currencyOut, {
                    maxNumResults,
                    maxHops: maxHops - 1,
                }, [...currentPaths, pair.pathOf(amountIn.currency)], originalAmountIn, bestTrades);
            }
        }
        return bestTrades;
    }
}
exports.Trade = Trade;
